/**
 * Injects a nonced <style> block with CSS generated from Vuex custom_styles (StoreFront theme).
 * Replaces inline styleMe usage for CSP compliance. Runs on client; updates when SET_CUSTOM_STYLES is committed.
 */

import { getCspNonce, camelToKebab } from '~/utils/styleToClass.js'

const STYLE_ELEMENT_ID = 'store-front-theme-styles'

function sanitizeClassPart(part) {
  return String(part || '').replace(/[\s.]+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '') || 'x'
}

/** @returns {{ block: string, bgColorDecl: string }} */
function declarationsFromStyleObj(styleObj) {
  const parts = []
  let bgColorDecl = ''
  if (!styleObj || typeof styleObj !== 'object') return { block: '', bgColorDecl: '' }
  for (const prop of Object.keys(styleObj)) {
    const cssProp = camelToKebab(prop)
    const value = styleObj[prop]
    if (value == null || value === '') continue
    const val = `${value} !important`
    const decl = `${cssProp}:${val}`
    parts.push(decl)
    if (cssProp === 'background-color') bgColorDecl = decl
  }
  return { block: parts.join(';'), bgColorDecl }
}

function buildCssFromCustomStyles(customStyles) {
  if (!customStyles || typeof customStyles !== 'object') return ''
  const rules = []
  for (const theme of Object.keys(customStyles)) {
    const comps = customStyles[theme]
    if (!comps || typeof comps !== 'object') continue
    for (const component of Object.keys(comps)) {
      const elements = comps[component]
      if (!elements || typeof elements !== 'object') continue
      for (const elementId of Object.keys(elements)) {
        const styleObj = elements[elementId]
        const { block, bgColorDecl } = declarationsFromStyleObj(styleObj)
        if (!block) continue
        const themePart = sanitizeClassPart(theme)
        const compPart = sanitizeClassPart(component)
        const elemPart = sanitizeClassPart(elementId)
        const className = `sf-${themePart}-${compPart}-${elemPart}`
        if (elementId === 'background') {
          rules.push(`body.${className}{${block}}`)
          const nonBody = [bgColorDecl, 'background-image:none !important'].filter(Boolean).join(';')
          rules.push(`.${className}:not(body){${nonBody}}`)
        } else {
          rules.push(`.${className}{${block}}`)
        }
      }
    }
  }
  // Personalise.vue: delivery option selected state uses button background-color
  const vanilla = customStyles.vanilla
  const storeFront = vanilla && vanilla.StoreFront
  const buttonStyles = storeFront && storeFront.button
  const buttonBg = buttonStyles && (buttonStyles['background-color'] || buttonStyles.backgroundColor)
  if (buttonBg) {
    rules.push('.delivery-option{border-color:#EDF2F4 !important}')
    rules.push(`.delivery-option.selected{border-color:${buttonBg} !important}`)
    rules.push('.schedule-option{border-color:#EDF2F4 !important}')
    rules.push(`.schedule-option.selected{border-color:${buttonBg} !important}`)
  }
  rules.push('.add-message-label{padding:8px 0}')
  rules.push('.icon-h-16{height:16px}')
  rules.push('.spacer-mt-16{margin-top:16px}')
  rules.push('.icon-px-4{padding-left:4px;padding-right:4px}')
  rules.push('.min-max-hint{float:right;color:grey;font-size:11px}')
  return rules.join('\n')
}

function injectThemeStyles(store) {
  if (typeof document === 'undefined' || !store || !store.state) return
  const customStyles = store.state.custom_styles
  const css = buildCssFromCustomStyles(customStyles)
  let el = document.getElementById(STYLE_ELEMENT_ID)
  if (!el) {
    el = document.createElement('style')
    el.id = STYLE_ELEMENT_ID
    const nonce = getCspNonce()
    if (nonce) el.setAttribute('nonce', nonce)
    document.head.appendChild(el)
  }
  el.textContent = css
}

export default function (context) {
  if (!process.client) return
  const { store } = context
  injectThemeStyles(store)
  store.subscribe((mutation) => {
    if (mutation.type === 'SET_CUSTOM_STYLES') {
      injectThemeStyles(store)
    }
  })
}
